/**
 * TravelCardPage — "Tarjeta viajera"
 * Credencial visual personal del usuario que representa su identidad viajera.
 *
 * Estado A: Placeholder (tarjeta vacía esperando ser creada)
 * Estado B: Tarjeta creada con preview visual y modal premium
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { trackTravelCardAction } from "../utils/analytics";
import {
	Box,
	Typography,
	IconButton,
	Button,
	Tooltip,
	Dialog,
	TextField,
	Snackbar,
	Alert,
	CircularProgress,
	Fade,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
	LuPlane,
	LuGlobe2,
	LuSparkles,
	LuCompass,
	LuGlobe,
	LuMap,
} from "react-icons/lu";
import html2canvas from "html2canvas";
import { useFirebase } from "../context/ApiContext";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import {
	DEMO_TRAVEL_CARD,
	DEMO_TRAVEL_CARD_COUNTRIES,
} from "../data/demoTravelCard";
import CardPaper from "../assets/passport/card-paper.png";
import TarjetaBg1 from "../assets/tarjetas/background-1.png";
import TarjetaBg2 from "../assets/tarjetas/background-2.png";
import TarjetaBg3 from "../assets/tarjetas/background-3.png";
import TarjetaBg4 from "../assets/tarjetas/background-4.png";
import TarjetaBg5 from "../assets/tarjetas/background-5.png";
import TarjetaBg6 from "../assets/tarjetas/background-6.png";
import TarjetaBg7 from "../assets/tarjetas/background-7.png";
import TarjetaBg8 from "../assets/tarjetas/background-8.png";

// ─── Paleta (tarjeta = identidad personal, no documento oficial) ───

// Ancho de tarjeta en export (mismo que pantalla para que el layout sea idéntico)
const EXPORT_CARD_WIDTH = 520;
const EXPORT_CARD_HEIGHT = Math.round(EXPORT_CARD_WIDTH / 1.7); // aspect ratio 1.7:1

// ─── Estilos de tarjeta — lifestyle / member card ───
const CARD_STYLES = [
	{
		id: "classic",
		label: "Obsidiana",
		bg: "linear-gradient(160deg, #1a1a2e 0%, #16213e 45%, #0f3460 100%)",
		accent: "#c9a84c",
		textPrimary: "#f5f0e8",
		textSecondary: "rgba(245,240,232,0.55)",
		textMuted: "rgba(245,240,232,0.3)",
		stampBg: "rgba(245,240,232,0.06)",
		stampBorder: "rgba(245,240,232,0.1)",
		stampRing: "#1a1a2e",
		badgeBg: "rgba(201,168,76,0.12)",
		badgeText: "#c9a84c",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(201,168,76,0.08) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #1a1a2e 0%, #0f3460 100%)",
		textureOpacity: 0.07,
	},
	{
		id: "burgundy",
		label: "Burdeos",
		bg: "linear-gradient(160deg, #1f0a0e 0%, #3d1522 45%, #5a1a30 100%)",
		accent: "#e8b08a",
		textPrimary: "#f8ede4",
		textSecondary: "rgba(248,237,228,0.55)",
		textMuted: "rgba(248,237,228,0.3)",
		stampBg: "rgba(248,237,228,0.06)",
		stampBorder: "rgba(248,237,228,0.1)",
		stampRing: "#2a0f14",
		badgeBg: "rgba(232,176,138,0.12)",
		badgeText: "#e8b08a",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(232,176,138,0.08) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #1f0a0e 0%, #5a1a30 100%)",
		textureOpacity: 0.07,
	},
	{
		id: "forest",
		label: "Bosque",
		bg: "linear-gradient(160deg, #0b1a10 0%, #15301c 45%, #1d4a28 100%)",
		accent: "#a8c68f",
		textPrimary: "#eef2ea",
		textSecondary: "rgba(238,242,234,0.55)",
		textMuted: "rgba(238,242,234,0.3)",
		stampBg: "rgba(238,242,234,0.06)",
		stampBorder: "rgba(238,242,234,0.1)",
		stampRing: "#0f2014",
		badgeBg: "rgba(168,198,143,0.12)",
		badgeText: "#a8c68f",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(168,198,143,0.08) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #0b1a10 0%, #1d4a28 100%)",
		textureOpacity: 0.07,
	},
	{
		id: "midnight",
		label: "Noche",
		bg: "linear-gradient(160deg, #0e0e1e 0%, #1a1a40 45%, #272760 100%)",
		accent: "#9ba8d4",
		textPrimary: "#eaecf5",
		textSecondary: "rgba(234,236,245,0.55)",
		textMuted: "rgba(234,236,245,0.3)",
		stampBg: "rgba(234,236,245,0.06)",
		stampBorder: "rgba(234,236,245,0.1)",
		stampRing: "#12122a",
		badgeBg: "rgba(155,168,212,0.12)",
		badgeText: "#9ba8d4",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(155,168,212,0.08) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #0e0e1e 0%, #272760 100%)",
		textureOpacity: 0.07,
	},
	// Pastel y claros
	{
		id: "lavender",
		label: "Lavanda",
		bg: "linear-gradient(160deg, #e8e4f4 0%, #d4cce8 45%, #b8a9d4 100%)",
		accent: "#6b5b95",
		textPrimary: "#2d2640",
		textSecondary: "rgba(45,38,64,0.7)",
		textMuted: "rgba(45,38,64,0.45)",
		stampBg: "rgba(107,91,149,0.08)",
		stampBorder: "rgba(107,91,149,0.2)",
		stampRing: "#e0daf0",
		badgeBg: "rgba(107,91,149,0.15)",
		badgeText: "#5a4a7a",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.5) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #e8e4f4 0%, #b8a9d4 100%)",
		textureOpacity: 0.5,
	},
	{
		id: "sand",
		label: "Arena",
		bg: "linear-gradient(160deg, #f5f0e6 0%, #e8dfd0 45%, #d4c4a8 100%)",
		accent: "#8b7355",
		textPrimary: "#2c2620",
		textSecondary: "rgba(44,38,32,0.65)",
		textMuted: "rgba(44,38,32,0.4)",
		stampBg: "rgba(139,115,85,0.08)",
		stampBorder: "rgba(139,115,85,0.18)",
		stampRing: "#ebe4d8",
		badgeBg: "rgba(139,115,85,0.14)",
		badgeText: "#6b5a45",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.6) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #f5f0e6 0%, #d4c4a8 100%)",
		textureOpacity: 0.5,
	},
	{
		id: "sage",
		label: "Salvia",
		bg: "linear-gradient(160deg, #e8efe6 0%, #d4dfd0 45%, #b8c8b0 100%)",
		accent: "#4a6b42",
		textPrimary: "#1e2a1b",
		textSecondary: "rgba(30,42,27,0.65)",
		textMuted: "rgba(30,42,27,0.4)",
		stampBg: "rgba(74,107,66,0.08)",
		stampBorder: "rgba(74,107,66,0.2)",
		stampRing: "#dce5d9",
		badgeBg: "rgba(74,107,66,0.14)",
		badgeText: "#3d5a36",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.5) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #e8efe6 0%, #b8c8b0 100%)",
		textureOpacity: 0.5,
	},
	{
		id: "blush",
		label: "Rosa",
		bg: "linear-gradient(160deg, #f8e8e8 0%, #f0d4d8 45%, #e4b8c0 100%)",
		accent: "#a85a6a",
		textPrimary: "#2d2024",
		textSecondary: "rgba(45,32,36,0.65)",
		textMuted: "rgba(45,32,36,0.4)",
		stampBg: "rgba(168,90,106,0.08)",
		stampBorder: "rgba(168,90,106,0.2)",
		stampRing: "#f2e2e4",
		badgeBg: "rgba(168,90,106,0.14)",
		badgeText: "#8b4a58",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.5) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #f8e8e8 0%, #e4b8c0 100%)",
		textureOpacity: 0.5,
	},
	{
		id: "sky",
		label: "Cielo",
		bg: "linear-gradient(160deg, #e4eef8 0%, #d0e0f0 45%, #b0c8e0 100%)",
		accent: "#4a6b8b",
		textPrimary: "#1a2433",
		textSecondary: "rgba(26,36,51,0.65)",
		textMuted: "rgba(26,36,51,0.4)",
		stampBg: "rgba(74,107,139,0.08)",
		stampBorder: "rgba(74,107,139,0.2)",
		stampRing: "#dce8f4",
		badgeBg: "rgba(74,107,139,0.14)",
		badgeText: "#3d5a75",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.5) 0%, transparent 60%)",
		preview: "linear-gradient(160deg, #e4eef8 0%, #b0c8e0 100%)",
		textureOpacity: 0.5,
	},
];

// ─── Fondos de imagen (assets/tarjetas) ───
const DARK_IMAGE_DEFAULTS = {
	overlay: "linear-gradient(160deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)",
	accent: "#e8d4a8",
	textPrimary: "#f5f0e8",
	textSecondary: "rgba(245,240,232,0.85)",
	textMuted: "rgba(245,240,232,0.5)",
	stampBg: "rgba(245,240,232,0.08)",
	stampBorder: "rgba(245,240,232,0.2)",
	stampRing: "rgba(20,20,30,0.6)",
	badgeBg: "rgba(232,212,168,0.2)",
	badgeText: "#e8d4a8",
	highlightGradient:
		"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.06) 0%, transparent 60%)",
	textureOpacity: 0.04,
};

const IMAGE_CARD_STYLES = [
	{
		id: "bg-1",
		label: "Fondo 1",
		bgImage: TarjetaBg1,
		overlay: null,
		accent: "#4a6b8a",
		textPrimary: "#2a3545",
		textSecondary: "rgba(42,53,69,0.82)",
		textMuted: "rgba(42,53,69,0.5)",
		stampBg: "rgba(42,53,69,0.08)",
		stampBorder: "rgba(42,53,69,0.2)",
		stampRing: "rgba(180,195,215,0.5)",
		badgeBg: "rgba(74,107,138,0.14)",
		badgeText: "#3d5a75",
		highlightGradient: "none",
		textureOpacity: 0,
	},
	{
		id: "bg-2",
		label: "Fondo 2",
		bgImage: TarjetaBg2,
		overlay: null,
		accent: "#6b7a5a",
		textPrimary: "#2e3828",
		textSecondary: "rgba(46,56,40,0.78)",
		textMuted: "rgba(46,56,40,0.45)",
		stampBg: "rgba(46,56,40,0.07)",
		stampBorder: "rgba(46,56,40,0.18)",
		stampRing: "rgba(190,200,175,0.5)",
		badgeBg: "rgba(90,107,74,0.13)",
		badgeText: "#4a5c3d",
		highlightGradient: "none",
		textureOpacity: 0,
	},
	{
		id: "bg-3",
		label: "Fondo 3",
		bgImage: TarjetaBg3,
		overlay: null,
		accent: "#8b7aa8",
		textPrimary: "#3d3548",
		textSecondary: "rgba(61,53,72,0.78)",
		textMuted: "rgba(61,53,72,0.45)",
		stampBg: "rgba(61,53,72,0.07)",
		stampBorder: "rgba(61,53,72,0.18)",
		stampRing: "rgba(200,195,215,0.5)",
		badgeBg: "rgba(107,91,133,0.13)",
		badgeText: "#5a4a6e",
		highlightGradient: "none",
		textureOpacity: 0,
	},
	{
		id: "bg-4",
		label: "Fondo 4",
		bgImage: TarjetaBg4,
		overlay: null,
		accent: "#8a6b7a",
		textPrimary: "#3d3238",
		textSecondary: "rgba(61,50,56,0.8)",
		textMuted: "rgba(61,50,56,0.48)",
		stampBg: "rgba(61,50,56,0.07)",
		stampBorder: "rgba(61,50,56,0.18)",
		stampRing: "rgba(210,195,200,0.5)",
		badgeBg: "rgba(138,107,122,0.14)",
		badgeText: "#5a4a52",
		highlightGradient: "none",
		textureOpacity: 0,
	},

	{
		id: "bg-5",
		label: "Fondo 5",
		bgImage: TarjetaBg5,
		titleBold: true,
		overlay:
			"linear-gradient(160deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.48) 100%)",
		accent: "#e8d4a8",
		textPrimary: "#f5f0e8",
		textSecondary: "rgba(245,240,232,0.88)",
		textMuted: "rgba(245,240,232,0.55)",
		stampBg: "rgba(245,240,232,0.08)",
		stampBorder: "rgba(245,240,232,0.2)",
		stampRing: "rgba(20,20,30,0.6)",
		badgeBg: "rgba(232,212,168,0.2)",
		badgeText: "#e8d4a8",
		highlightGradient:
			"radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.06) 0%, transparent 60%)",
		textureOpacity: 0.04,
	},
	{
		id: "bg-6",
		label: "Fondo 6",
		bgImage: TarjetaBg6,
		overlay: null,
		accent: "#5a7a7a",
		textPrimary: "#2a3838",
		textSecondary: "rgba(42,56,56,0.8)",
		textMuted: "rgba(42,56,56,0.48)",
		stampBg: "rgba(42,56,56,0.07)",
		stampBorder: "rgba(42,56,56,0.18)",
		stampRing: "rgba(190,205,205,0.5)",
		badgeBg: "rgba(90,122,122,0.14)",
		badgeText: "#4a5c5c",
		highlightGradient: "none",
		textureOpacity: 0,
	},
	{
		id: "bg-7",
		label: "Fondo 7",
		bgImage: TarjetaBg7,
		overlay: null,
		accent: "#8a6b7a",
		textPrimary: "#3d3238",
		textSecondary: "rgba(61,50,56,0.8)",
		textMuted: "rgba(61,50,56,0.48)",
		stampBg: "rgba(61,50,56,0.07)",
		stampBorder: "rgba(61,50,56,0.18)",
		stampRing: "rgba(210,195,200,0.5)",
		badgeBg: "rgba(138,107,122,0.14)",
		badgeText: "#5a4a52",
		highlightGradient: "none",
		textureOpacity: 0,
	},
	{
		id: "bg-8",
		label: "Fondo 8",
		bgImage: TarjetaBg8,
		overlay: null,
		accent: "#8b6914",
		textPrimary: "#2c2520",
		textSecondary: "rgba(44,37,32,0.82)",
		textMuted: "rgba(44,37,32,0.5)",
		stampBg: "rgba(44,37,32,0.07)",
		stampBorder: "rgba(44,37,32,0.2)",
		stampRing: "rgba(195,185,170,0.5)",
		badgeBg: "rgba(139,105,20,0.14)",
		badgeText: "#6b5010",
		highlightGradient: "none",
		textureOpacity: 0,
	},
].map((item) => ({
	...DARK_IMAGE_DEFAULTS,
	...item,
	preview: null,
}));

// Lista unificada: gradientes + fondos de imagen
const ALL_CARD_STYLES = [...CARD_STYLES, ...IMAGE_CARD_STYLES];

// ─── Helpers ───
const getStorageKey = (userId) => `travel_card_${userId}`;

// Nivel viajero — evolución natural, no gamificación (iconos, no emojis)
const TRAVELER_LEVELS = [
	{ label: "Viajero", Icon: LuPlane },
	{ label: "Explorador", Icon: LuCompass },
	{ label: "Gran explorador", Icon: LuGlobe2 },
	{ label: "Viajero experto", Icon: LuMap },
	{ label: "Globetrotter", Icon: LuGlobe },
	{ label: "Ciudadano del mundo", Icon: LuGlobe },
];
const getTravelerLevel = (count) => {
	if (count < 5) return TRAVELER_LEVELS[0];
	if (count < 15) return TRAVELER_LEVELS[1];
	if (count < 30) return TRAVELER_LEVELS[2];
	if (count < 50) return TRAVELER_LEVELS[3];
	if (count < 80) return TRAVELER_LEVELS[4];
	return TRAVELER_LEVELS[5];
};

// Rotación sutil determinista para cada sello (sensación de sellos físicos)
const getStampRotation = (code, index) => {
	const seed =
		(code.charCodeAt(0) || 65) * 7 +
		(code.charCodeAt(1) || 65) * 13 +
		index * 3;
	return ((seed % 11) - 5) * 0.8; // rango: ~-4° a +4°
};

const downloadBlob = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

// ═══════════════════════════════════════════════
// TARJETA VISUAL — credencial de miembro / lifestyle card
// Diferenciada del pasaporte: editorial, aireada, moderna
// ═══════════════════════════════════════════════
const TravelCardVisual = React.forwardRef(
	(
		{ cardData, countries, style: cardStyle, scale = 1, isExport = false },
		ref,
	) => {
		const { name, phrase } = cardData;
		const visibleStamps = countries.slice(0, 7);
		const extraCount = Math.max(0, countries.length - 7);
		const e = isExport; // shorthand
		const { label: levelLabel, Icon: LevelIcon } = getTravelerLevel(
			countries.length,
		);

		const isImageBg = !!cardStyle.bgImage;

		return (
			<Box
				ref={ref}
				sx={{
					width: e ? EXPORT_CARD_WIDTH : "100%",
					maxWidth: e ? EXPORT_CARD_WIDTH : 520,
					...(e ? { height: EXPORT_CARD_HEIGHT } : { aspectRatio: "1.7 / 1" }),
					borderRadius: "20px",
					overflow: "hidden",
					position: "relative",
					...(!isImageBg && { background: cardStyle.bg }),
					boxShadow: e
						? "none"
						: "0 24px 64px rgba(0,0,0,0.25), 0 4px 16px rgba(0,0,0,0.1)",
					transform: e ? "none" : `scale(${scale})`,
					transformOrigin: "center center",
				}}
			>
				{/* === Fondo de imagen como <img> para que html2canvas use resolución nativa === */}
				{isImageBg && (
					<img
						src={cardStyle.bgImage}
						alt=""
						style={{
							position: "absolute",
							inset: 0,
							width: "100%",
							height: "100%",
							objectFit: "cover",
							objectPosition: "center",
							pointerEvents: "none",
							display: "block",
						}}
					/>
				)}
				{/* === Overlay cuando el fondo es imagen (legibilidad) === */}
				{isImageBg && cardStyle.overlay && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							background: cardStyle.overlay,
							pointerEvents: "none",
						}}
					/>
				)}
				{/* === Capa 1: Textura papel — 0.1 oscuros, 0.5 pastel === */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						backgroundImage: `url(${CardPaper})`,
						backgroundSize: "cover",
						opacity: cardStyle.textureOpacity ?? 0.1,
						pointerEvents: "none",
					}}
				/>

				{/* === Capa 2: Micro-noise casi imperceptible === */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						backgroundImage:
							"radial-gradient(circle at 1px 1px, rgba(255,255,255,0.02) 1px, transparent 1px)",
						backgroundSize: "4px 4px",
						pointerEvents: "none",
					}}
				/>

				{/* === Capa 3: Highlight superior — reflejo de luz suave === */}
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						background: cardStyle.highlightGradient,
						pointerEvents: "none",
					}}
				/>

				{/* === Capa 4: Brillo de borde superior (efecto de objeto real) === */}
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: "10%",
						right: "10%",
						height: "1px",
						background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)`,
						pointerEvents: "none",
					}}
				/>

				{/* === Contenido principal — layout editorial asimétrico === */}
				<Box
					sx={{
						position: "relative",
						zIndex: 1,
						height: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						p: e ? 3.5 : { xs: 2.5, sm: 3.5 },
					}}
				>
					{/* ── Top: Badge destinos + etiqueta ── */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
						}}
					>
						{/* Etiqueta tipo */}
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
							<LuGlobe2
								style={{
									color: cardStyle.accent,
									fontSize: 12,
									opacity: 0.6,
								}}
							/>
							<Typography
								sx={{
									color: cardStyle.textMuted,
									fontSize: e ? 9.5 : { xs: 8, sm: 9.5 },
									fontWeight: `${cardStyle.titleBold ? 700 : 500} !important`,
									letterSpacing: "0.18em",
									textTransform: "uppercase",
									fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
								}}
							>
								Tarjeta Viajera
							</Typography>
						</Box>

						{/* Badge nivel viajero — aspiracional, discreto */}
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								backgroundColor: cardStyle.badgeBg,
								borderRadius: "10px",
								px: e ? 1.5 : { xs: 1.2, sm: 1.5 },
								py: e ? 0.5 : { xs: 0.4, sm: 0.5 },
							}}
						>
							<LevelIcon
								style={{
									color: cardStyle.badgeText,
									fontSize: 11,
									opacity: 0.9,
									flexShrink: 0,
								}}
							/>
							<Typography
								sx={{
									color: cardStyle.badgeText,
									fontSize: e ? 10 : { xs: 8.5, sm: 10 },
									fontWeight: 600,
									letterSpacing: "0.04em",
									lineHeight: 1.2,
									fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
									whiteSpace: "nowrap",
								}}
							>
								{levelLabel}
							</Typography>
						</Box>
					</Box>

					{/* ── Center: Nombre + frase — jerarquía editorial ── */}
					<Box
						sx={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
						}}
					>
						<Typography
							sx={{
								color: cardStyle.textPrimary,
								fontSize: e ? 34 : { xs: 26, sm: 34 },
								fontWeight: 700,
								letterSpacing: "-0.02em",
								lineHeight: 1.1,
								fontFamily: "'Playfair Display', Georgia, serif",
							}}
						>
							{name}
						</Typography>
						{phrase && (
							<Typography
								sx={{
									color: cardStyle.textSecondary,
									fontSize: e ? 13 : { xs: 11, sm: 13 },
									fontWeight: 400,
									fontStyle: "italic",
									letterSpacing: "0.01em",
									mt: 0.8,
									maxWidth: "80%",
									fontFamily: "'Playfair Display', Georgia, serif",
								}}
							>
								{phrase}
							</Typography>
						)}
					</Box>

					{/* ── Bottom: Mini sellos orgánicos + branding ── */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-end",
						}}
					>
						{/* Mini sellos de viaje — superpuestos, rotación sutil, sensación física */}
						<Box sx={{ display: "flex", alignItems: "center", pl: 0.3 }}>
							{visibleStamps.map((country, i) => {
								const code = (country.countryCode || "").toUpperCase();
								const rotation = getStampRotation(code, i);
								return (
									<Box
										key={country.id || i}
										sx={{
											width: e ? 35 : { xs: 30, sm: 35 },
											height: e ? 35 : { xs: 30, sm: 35 },
											borderRadius: "50%",
											border: `1.5px solid ${cardStyle.stampBorder}`,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: cardStyle.stampRing,
											boxShadow: `inset 0 0 0 1px ${cardStyle.stampBorder}, 0 1px 4px rgba(0,0,0,0.15)`,
											transform: `rotate(${rotation}deg)`,
											ml: i === 0 ? 0 : e ? "-6px" : { xs: "-5px", sm: "-6px" },
											position: "relative",
											zIndex: visibleStamps.length - i,
											flexShrink: 0,
										}}
									>
										<Typography
											sx={{
												color: cardStyle.textSecondary,
												fontSize: e ? 10.5 : { xs: 9, sm: 10.5 },
												fontWeight: 700,
												letterSpacing: "0.05em",
												lineHeight: 1,
												fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
											}}
										>
											{code}
										</Typography>
									</Box>
								);
							})}
							{extraCount > 0 && (
								<Box
									sx={{
										width: e ? 35 : { xs: 30, sm: 35 },
										height: e ? 35 : { xs: 30, sm: 35 },
										borderRadius: "50%",
										border: `1px solid ${cardStyle.stampBorder}`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: cardStyle.stampRing,
										ml: e ? "-6px" : { xs: "-5px", sm: "-6px" },
										position: "relative",
										zIndex: 0,
										opacity: 0.4,
										filter: "blur(0.6px)",
										flexShrink: 0,
									}}
								>
									<Typography
										sx={{
											color: cardStyle.textMuted,
											fontSize: e ? 9 : { xs: 8, sm: 9 },
											fontWeight: 600,
											lineHeight: 1,
											fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
										}}
									>
										+{extraCount}
									</Typography>
								</Box>
							)}
						</Box>

						{/* Conteo destinos (protagonista) + branding */}
						<Box
							sx={{
								textAlign: "right",
								flexShrink: 0,
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								...(cardStyle.id === "bg-8" && {
									backgroundColor: "rgba(255, 220, 100, 0.92)",
									borderRadius: "12px",
									px: 1.5,
									py: 1,
									boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
								}),
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									gap: 0.5,
								}}
							>
								<Typography
									sx={{
										color: cardStyle.accent,
										fontSize: e ? 30 : { xs: 24, sm: 30 },
										fontWeight: 700,
										lineHeight: 1,
										fontFamily: "'Playfair Display', Georgia, serif",
										letterSpacing: "-0.02em",
									}}
								>
									{countries.length}
								</Typography>
								<Typography
									sx={{
										color: cardStyle.textSecondary,
										fontSize: e ? 9.5 : { xs: 8, sm: 9.5 },
										fontWeight: 500,
										letterSpacing: "0.06em",
										textTransform: "uppercase",
										fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
										lineHeight: 1,
										pb: 0.2,
									}}
								>
									{countries.length === 1 ? "destino" : "destinos"}
								</Typography>
							</Box>
							<Typography
								sx={{
									color: cardStyle.textMuted,
									fontSize: e ? 7.5 : { xs: 6.5, sm: 7.5 },
									fontWeight: 500,
									letterSpacing: "0.14em",
									textTransform: "uppercase",
									fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
									mt: 0.3,
								}}
							>
								Viajes Molones
							</Typography>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	},
);

// ═══════════════════════════════════════════════
// PLACEHOLDER (Estado A — tarjeta no creada)
// ═══════════════════════════════════════════════
const TravelCardPlaceholder = ({ onClick }) => {
	return (
		<Box
			onClick={onClick}
			sx={{
				width: "100%",
				maxWidth: 520,
				aspectRatio: "1.7 / 1",
				borderRadius: "20px",
				border: "2px dashed rgba(99,102,241,0.25)",
				background:
					"linear-gradient(135deg, rgba(99,102,241,0.03) 0%, rgba(139,92,246,0.03) 100%)",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				cursor: "pointer",
				transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
				position: "relative",
				overflow: "hidden",
				"&:hover": {
					border: "2px dashed rgba(99,102,241,0.45)",
					background:
						"linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(139,92,246,0.06) 100%)",
					transform: "translateY(-4px)",
					boxShadow: "0 12px 40px rgba(99,102,241,0.12)",
					"& .placeholder-icon": {
						transform: "scale(1.1)",
					},
					"& .placeholder-text": {
						color: "#4f46e5",
					},
				},
			}}
		>
			{/* Esquinas decorativas punteadas */}
			{["top-left", "top-right", "bottom-left", "bottom-right"].map(
				(corner) => {
					const isTop = corner.includes("top");
					const isLeft = corner.includes("left");
					return (
						<Box
							key={corner}
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 16,
								[isLeft ? "left" : "right"]: 16,
								width: 16,
								height: 16,
								pointerEvents: "none",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									[isTop ? "top" : "bottom"]: 0,
									[isLeft ? "left" : "right"]: 0,
									width: 16,
									height: "1.5px",
									background: `linear-gradient(${
										isLeft ? "90deg" : "270deg"
									}, rgba(99,102,241,0.3), transparent)`,
								}}
							/>
							<Box
								sx={{
									position: "absolute",
									[isTop ? "top" : "bottom"]: 0,
									[isLeft ? "left" : "right"]: 0,
									width: "1.5px",
									height: 16,
									background: `linear-gradient(${
										isTop ? "180deg" : "0deg"
									}, rgba(99,102,241,0.3), transparent)`,
								}}
							/>
						</Box>
					);
				},
			)}

			<Box
				className="placeholder-icon"
				sx={{
					width: 56,
					height: 56,
					borderRadius: "50%",
					border: "1.5px solid rgba(99,102,241,0.15)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mb: 2,
					transition: "transform 0.3s ease",
					background:
						"radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)",
				}}
			>
				<LuPlane style={{ fontSize: 24, color: "#6366F1", opacity: 0.6 }} />
			</Box>

			<Typography
				className="placeholder-text"
				sx={{
					color: "#64748b",
					fontSize: { xs: "0.95rem", sm: "1.05rem" },
					fontWeight: 600,
					fontFamily: "'Playfair Display', Georgia, serif",
					letterSpacing: "0.02em",
					transition: "color 0.3s ease",
				}}
			>
				Crea tu tarjeta viajera
			</Typography>

			<Typography
				sx={{
					color: "#94a3b8",
					fontSize: "0.8rem",
					fontWeight: 400,
					mt: 0.5,
					letterSpacing: "0.01em",
				}}
			>
				Tu identidad viajera en una tarjeta
			</Typography>
		</Box>
	);
};

// ═══════════════════════════════════════════════
// CREADOR DE TARJETA (inline dialog)
// ═══════════════════════════════════════════════
const TravelCardCreator = ({
	open,
	onClose,
	onSave,
	userName,
	existingCard,
}) => {
	const isEdit = !!existingCard;
	const [name, setName] = useState(existingCard?.name || userName || "");
	const [phrase, setPhrase] = useState(existingCard?.phrase || "");
	const [selectedStyle, setSelectedStyle] = useState(
		existingCard?.styleId || "classic",
	);

	useEffect(() => {
		if (open) {
			if (existingCard) {
				setName(existingCard.name || userName || "");
				setPhrase(existingCard.phrase || "");
				setSelectedStyle(existingCard.styleId || "classic");
			} else {
				setName(userName || "");
				setPhrase("");
				setSelectedStyle("classic");
			}
		}
	}, [open, userName, existingCard]);

	const handleSave = () => {
		if (!name.trim()) return;
		onSave(
			{
				name: name.trim(),
				phrase: phrase.trim(),
				styleId: selectedStyle,
				createdAt: existingCard?.createdAt ?? Date.now(),
			},
			{ isEdit },
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: "20px",
					background: "#fff",
					overflow: "hidden",
					maxWidth: 480,
					// En móvil: altura máxima, márgenes y layout flex para permitir scroll del body
					display: "flex",
					flexDirection: "column",
					maxHeight: { xs: "85vh", sm: "none" },
					margin: { xs: "12px 16px", sm: "auto" },
				},
			}}
		>
			{/* Header — fijo, sin scroll */}
			<Box
				sx={{
					p: 3,
					pb: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexShrink: 0,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: "12px",
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<LuSparkles style={{ color: "#fff", fontSize: 20 }} />
					</Box>
					<Box>
						<Typography
							sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e" }}
						>
							{isEdit ? "Edita tu tarjeta" : "Crea tu tarjeta"}
						</Typography>
						<Typography sx={{ fontSize: "0.78rem", color: "#8B8FA3" }}>
							{isEdit
								? "Actualiza tu identidad viajera"
								: "Personaliza tu identidad viajera"}
						</Typography>
					</Box>
				</Box>
				<IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Body — scrolleable en móvil */}
			<Box
				sx={{
					px: 3,
					pb: 3,
					overflowY: "auto",
					flex: "1 1 auto",
					minHeight: 0,
					WebkitOverflowScrolling: "touch",
				}}
			>
				{/* Nombre */}
				<Typography
					sx={{
						fontSize: "0.8rem",
						fontWeight: 600,
						color: "#4B5563",
						mb: 0.8,
						mt: 1,
					}}
				>
					Tu nombre
				</Typography>
				<TextField
					fullWidth
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="Nombre visible en la tarjeta"
					variant="outlined"
					size="small"
					inputProps={{ maxLength: 30 }}
					sx={{
						mb: 2,
						"& .MuiOutlinedInput-root": {
							borderRadius: "12px",
							backgroundColor: "#f8f9fb",
							"& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
							"&:hover fieldset": { borderColor: "rgba(99,102,241,0.3)" },
							"&.Mui-focused fieldset": { borderColor: "#6366F1" },
						},
					}}
				/>

				{/* Frase */}
				<Typography
					sx={{
						fontSize: "0.8rem",
						fontWeight: 600,
						color: "#4B5563",
						mb: 0.8,
					}}
				>
					Frase personal{" "}
					<span style={{ color: "#94a3b8", fontWeight: 400 }}>(opcional)</span>
				</Typography>
				<TextField
					fullWidth
					value={phrase}
					onChange={(e) => setPhrase(e.target.value)}
					placeholder="Ej: Explorando el mundo paso a paso"
					variant="outlined"
					size="small"
					inputProps={{ maxLength: 60 }}
					sx={{
						mb: 2.5,
						"& .MuiOutlinedInput-root": {
							borderRadius: "12px",
							backgroundColor: "#f8f9fb",
							"& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
							"&:hover fieldset": { borderColor: "rgba(99,102,241,0.3)" },
							"&.Mui-focused fieldset": { borderColor: "#6366F1" },
						},
					}}
				/>

				{/* Selector de estilo */}
				<Typography
					sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#4B5563", mb: 1 }}
				>
					Estilo{" "}
					{isEdit && (
						<span style={{ color: "#94a3b8", fontWeight: 400 }}>
							(el actual está marcado)
						</span>
					)}
				</Typography>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
					{ALL_CARD_STYLES.map((style) => {
						const isImageStyle = !!style.bgImage;
						return (
							<Box
								key={style.id}
								onClick={() => setSelectedStyle(style.id)}
								sx={{
									width: "calc(25% - 12px)",
									minWidth: 72,
									borderRadius: "12px",
									overflow: "hidden",
									cursor: "pointer",
									border:
										selectedStyle === style.id
											? "2px solid #6366F1"
											: "2px solid transparent",
									transition: "all 0.2s ease",
									"&:hover": {
										transform: "translateY(-2px)",
										boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
									},
								}}
							>
								<Box
									sx={{
										height: 48,
										...(isImageStyle
											? {
													backgroundImage: `url(${style.bgImage})`,
													backgroundSize: "cover",
													backgroundPosition: "center",
												}
											: { background: style.preview }),
										position: "relative",
										display: "flex",
										alignItems: "flex-end",
										justifyContent: "flex-start",
										px: 1,
										pb: 0.8,
									}}
								>
									{isImageStyle && (
										<Box
											sx={{
												position: "absolute",
												inset: 0,
												background:
													"linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.5) 100%)",
												pointerEvents: "none",
											}}
										/>
									)}
									{/* Mini text lines simulating card layout */}
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: "3px",
											position: "relative",
											zIndex: 1,
										}}
									>
										<Box
											sx={{
												width: 28,
												height: 3,
												borderRadius: 1,
												backgroundColor: `${style.textPrimary || "#fff"}44`,
											}}
										/>
										<Box
											sx={{
												width: 18,
												height: 2,
												borderRadius: 1,
												backgroundColor: `${style.textPrimary || "#fff"}22`,
											}}
										/>
									</Box>
									<Box
										sx={{
											position: "absolute",
											top: 6,
											right: 8,
											width: 16,
											height: 10,
											borderRadius: "4px",
											backgroundColor:
												style.badgeBg || `${style.accent || "#6366F1"}18`,
											zIndex: 1,
										}}
									/>
								</Box>
								<Box
									sx={{
										p: 0.8,
										textAlign: "center",
										backgroundColor: "#f8f9fb",
									}}
								>
									<Typography
										sx={{
											fontSize: "0.7rem",
											fontWeight: 600,
											color: "#4B5563",
										}}
									>
										{style.label}
									</Typography>
								</Box>
							</Box>
						);
					})}
				</Box>

				{/* Botón crear / guardar — Box + Typography para que el texto sea siempre blanco */}
				<Box
					onClick={name.trim() ? handleSave : undefined}
					role="button"
					tabIndex={0}
					onKeyDown={(ev) => {
						if ((ev.key === "Enter" || ev.key === " ") && name.trim())
							handleSave();
					}}
					sx={{
						width: "100%",
						py: 1.5,
						borderRadius: "14px",
						background: name.trim()
							? "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)"
							: "#e5e7eb",
						boxShadow: name.trim()
							? "0 4px 16px rgba(99,102,241,0.35)"
							: "none",
						transition: "all 0.2s ease",
						cursor: name.trim() ? "pointer" : "default",
						opacity: name.trim() ? 1 : 0.8,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						"&:hover": name.trim()
							? {
									transform: "translateY(-2px)",
									boxShadow: "0 6px 20px rgba(99,102,241,0.45)",
								}
							: {},
					}}
				>
					<Typography
						component="span"
						sx={{
							color: "#fff",
							fontWeight: 600,
							fontSize: "0.95rem",
							textTransform: "none",
						}}
					>
						{isEdit ? "Guardar cambios" : "Crear mi tarjeta viajera"}
					</Typography>
				</Box>
			</Box>
		</Dialog>
	);
};

// ═══════════════════════════════════════════════
// MODAL PREMIUM (visualización grande)
// ═══════════════════════════════════════════════
const TravelCardModal = ({
	open,
	onClose,
	cardData,
	countries,
	cardStyle,
	onSnackbar,
	onRequireAuth,
}) => {
	const exportRef = useRef(null);
	const [downloading, setDownloading] = useState(false);

	const handleDownload = async () => {
		if (onRequireAuth?.()) return;
		if (!exportRef.current) return;
		setDownloading(true);
		trackTravelCardAction("exported", { style_id: cardData?.styleId });
		try {
			const canvas = await html2canvas(exportRef.current, {
				scale: 4,
				useCORS: true,
				allowTaint: true,
				backgroundColor: null,
				logging: false,
				imageTimeout: 0,
			});
			canvas.toBlob((blob) => {
				if (blob) {
					downloadBlob(
						blob,
						`tarjeta-viajera-${cardData.name
							.toLowerCase()
							.replace(/\s+/g, "-")}.png`,
					);
					onSnackbar?.({
						open: true,
						message: "Imagen descargada",
						severity: "success",
					});
				}
				setDownloading(false);
			}, "image/png");
		} catch (err) {
			console.error("Error al exportar:", err);
			setDownloading(false);
			onSnackbar?.({
				open: true,
				message: "Error al descargar",
				severity: "error",
			});
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 0,
					background: "transparent",
					boxShadow: "none",
					maxWidth: "100vw",
					maxHeight: "100vh",
					width: "100vw",
					height: "100vh",
					m: 0,
				},
			}}
			sx={{
				"& .MuiBackdrop-root": {
					backgroundColor: "rgba(10, 10, 20, 0.85)",
					backdropFilter: "blur(20px)",
				},
			}}
		>
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					position: "relative",
				}}
			>
				{/* Botón cerrar */}
				<IconButton
					onClick={onClose}
					sx={{
						position: "absolute",
						top: 20,
						right: 20,
						color: "rgba(255,255,255,0.6)",
						backgroundColor: "rgba(255,255,255,0.08)",
						border: "1px solid rgba(255,255,255,0.1)",
						"&:hover": {
							backgroundColor: "rgba(255,255,255,0.15)",
							color: "#fff",
						},
						zIndex: 10,
					}}
				>
					<CloseIcon />
				</IconButton>

				{/* Tarjeta grande con ligera inclinación */}
				<Box
					sx={{
						width: "90%",
						maxWidth: 600,
						transform: "rotate(-1.5deg)",
						transition: "transform 0.4s ease",
						"&:hover": {
							transform: "rotate(0deg) scale(1.02)",
						},
						mb: 4,
					}}
				>
					<TravelCardVisual
						cardData={cardData}
						countries={countries}
						style={cardStyle}
					/>
				</Box>

				{/* Acción: solo descargar imagen */}
				<Tooltip title="Descargar imagen">
					<IconButton
						onClick={handleDownload}
						disabled={downloading}
						sx={{
							color: "rgba(255,255,255,0.7)",
							backgroundColor: "rgba(255,255,255,0.08)",
							border: "1px solid rgba(255,255,255,0.12)",
							width: 52,
							height: 52,
							"&:hover": {
								backgroundColor: "rgba(255,255,255,0.15)",
								color: "#fff",
							},
						}}
					>
						{downloading ? (
							<CircularProgress
								size={20}
								sx={{ color: "rgba(255,255,255,0.7)" }}
							/>
						) : (
							<FileDownloadOutlinedIcon sx={{ fontSize: 22 }} />
						)}
					</IconButton>
				</Tooltip>

				{/* Tarjeta para exportar: mismo tamaño que en pantalla, sin espacio extra */}
				<Box
					sx={{
						position: "fixed",
						left: 0,
						top: 0,
						width: EXPORT_CARD_WIDTH,
						height: EXPORT_CARD_HEIGHT,
						overflow: "hidden",
						pointerEvents: "none",
						opacity: 0,
						zIndex: -1,
					}}
				>
					<TravelCardVisual
						ref={exportRef}
						cardData={cardData}
						countries={countries}
						style={cardStyle}
						isExport
					/>
				</Box>
			</Box>
		</Dialog>
	);
};

// ═══════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════
const TravelCardPage = () => {
	const navigate = useNavigate();
	const { user, getPassport, getTravelCard, saveTravelCard } = useFirebase();

	const [loading, setLoading] = useState(true);
	const [countries, setCountries] = useState([]);
	const [cardData, setCardData] = useState(null); // null = no creada
	const [creatorOpen, setCreatorOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("signup");
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const isDemo = !user;

	const requireAuth = (message) => {
		if (user) return false;
		setAuthMode("signup");
		setShowLogin(true);
		setSnackbar({
			open: true,
			message:
				message || "Crea una cuenta para guardar tu tarjeta.",
			severity: "info",
		});
		return true;
	};

	// Cargar datos (demo si no hay sesión; backend + migración si hay)
	useEffect(() => {
		const load = async () => {
			if (!user) {
				setCountries(DEMO_TRAVEL_CARD_COUNTRIES);
				setCardData(DEMO_TRAVEL_CARD);
				setLoading(false);
				return;
			}
			try {
				// Cargar pasaporte para obtener países
				const data = await getPassport();
				setCountries(data?.countries || []);

				// Cargar tarjeta desde backend
				let card = null;
				try {
					card = await getTravelCard();
				} catch (_) {}

				// Migración: si había tarjeta en localStorage y no en backend, subirla
				const localKey = getStorageKey(user.id || user.uid);
				const savedLocal = localStorage.getItem(localKey);
				if (savedLocal && !card) {
					try {
						const parsed = JSON.parse(savedLocal);
						if (parsed && parsed.name) {
							await saveTravelCard(parsed);
							card = parsed;
							localStorage.removeItem(localKey);
						}
					} catch (_) {}
				}

				if (card) setCardData(card);
			} catch (error) {
				console.error("Error cargando datos:", error);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [user, getPassport, getTravelCard, saveTravelCard]);

	const userName = useMemo(() => {
		if (!user) return DEMO_TRAVEL_CARD.name;
		return (
			user.displayName || user.name || user.email?.split("@")[0] || "Viajero"
		);
	}, [user]);

	const cardStyle = useMemo(() => {
		const styleId = cardData?.styleId || "classic";
		return ALL_CARD_STYLES.find((s) => s.id === styleId) || CARD_STYLES[0];
	}, [cardData?.styleId]);

	const handleSaveCard = async (data, options = {}) => {
		if (!user) {
			setCardData({ ...data, isDemo: true });
			setCreatorOpen(false);
			setSnackbar({
				open: true,
				message: "Así se vería. Crea una cuenta para guardarla.",
				severity: "info",
			});
			return;
		}
		try {
			const saved = await saveTravelCard(data);
			trackTravelCardAction(options.isEdit ? "edited" : "created", {
				style_id: data.styleId,
			});
			setCardData(saved || data);
			setCreatorOpen(false);
			setSnackbar({
				open: true,
				message: options.isEdit
					? "Tu tarjeta viajera ha sido actualizada"
					: "Tu tarjeta viajera ha sido creada",
				severity: "success",
			});
		} catch (err) {
			setSnackbar({
				open: true,
				message: "Error al guardar la tarjeta",
				severity: "error",
			});
		}
	};

	const handleEditCard = () => {
		setCreatorOpen(true);
	};

	if (loading) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#f7f7f7",
				}}
			>
				<Box sx={{ textAlign: "center" }}>
					<CircularProgress sx={{ color: "#6366F1" }} size={44} />
					<Typography sx={{ mt: 2, color: "#64748b", fontSize: "0.9rem" }}>
						Preparando tu tarjeta...
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "#f7f7f7",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* Patrón de fondo sutil */}
			<Box
				sx={{
					position: "fixed",
					inset: 0,
					backgroundImage: `
						radial-gradient(circle at 25% 25%, rgba(99,102,241,0.03) 0%, transparent 45%),
						radial-gradient(circle at 75% 75%, rgba(139,92,246,0.02) 0%, transparent 45%)
					`,
					pointerEvents: "none",
					zIndex: 0,
				}}
			/>

			{/* Botón volver */}
			<Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 100 }}>
				<Tooltip title="Volver al Dashboard">
					<IconButton
						onClick={() => navigate("/")}
						sx={{
							color: "#64748b",
							backgroundColor: "rgba(0,0,0,0.04)",
							border: "1px solid rgba(0,0,0,0.08)",
							"&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
						}}
					>
						<ArrowBackIosNewIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			{/* Botón editar (solo si tarjeta creada) */}
			{cardData && (
				<Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 100 }}>
					<Tooltip title="Editar tarjeta">
						<IconButton
							onClick={handleEditCard}
							sx={{
								color: "#64748b",
								backgroundColor: "rgba(0,0,0,0.04)",
								border: "1px solid rgba(0,0,0,0.08)",
								"&:hover": { backgroundColor: "rgba(0,0,0,0.08)" },
							}}
						>
							<EditOutlinedIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			)}

			{/* Título sutil */}
			<Box sx={{ textAlign: "center", mb: 4, position: "relative", zIndex: 1 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 1,
						mb: 1,
					}}
				>
					<LuPlane style={{ color: "#6366F1", fontSize: 16, opacity: 0.6 }} />
					<Typography
						sx={{
							color: "#8B8FA3",
							fontSize: "0.75rem",
							fontWeight: 600,
							letterSpacing: "0.2em",
							textTransform: "uppercase",
						}}
					>
						Tu identidad viajera
					</Typography>
				</Box>
				<Typography
					sx={{
						color: "#1a1a2e",
						fontSize: { xs: "1.5rem", sm: "1.8rem" },
						fontWeight: 700,
						fontFamily: "'Playfair Display', Georgia, serif",
						letterSpacing: "-0.01em",
					}}
				>
					Tarjeta Viajera
				</Typography>
				{isDemo && (
					<Typography
						onClick={() =>
							requireAuth("Crea una cuenta para guardar tu tarjeta.")
						}
						sx={{
							mt: 1.5,
							display: "inline-block",
							px: 2,
							py: 0.6,
							borderRadius: 999,
							backgroundColor: "rgba(99,102,241,0.12)",
							color: "#4f46e5",
							fontSize: "0.78rem",
							fontWeight: 600,
							cursor: "pointer",
							"&:hover": { backgroundColor: "rgba(99,102,241,0.18)" },
						}}
					>
						Ejemplo · Crea una cuenta para la tuya
					</Typography>
				)}
			</Box>

			{/* Contenido principal */}
			<Box
				sx={{
					position: "relative",
					zIndex: 1,
					width: "90%",
					maxWidth: 560,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				{!cardData ? (
					/* Estado A: Placeholder */
					<Fade in timeout={600}>
						<Box sx={{ width: "100%" }}>
							<TravelCardPlaceholder onClick={() => setCreatorOpen(true)} />
						</Box>
					</Fade>
				) : (
					/* Estado B: Tarjeta creada con ligera inclinación */
					<Fade in timeout={600}>
						<Box
							onClick={() => setModalOpen(true)}
							sx={{
								width: "100%",
								cursor: "pointer",
								transform: "perspective(800px) rotateY(-2deg) rotateX(1deg)",
								transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
								"&:hover": {
									transform:
										"perspective(800px) rotateY(0deg) rotateX(0deg) translateY(-6px)",
									"& .card-hint": {
										opacity: 1,
									},
								},
							}}
						>
							<TravelCardVisual
								cardData={cardData}
								countries={countries}
								style={cardStyle}
							/>
							{/* Hint visual */}
							<Typography
								className="card-hint"
								sx={{
									textAlign: "center",
									color: "#94a3b8",
									fontSize: "0.78rem",
									mt: 2,
									opacity: 0,
									transition: "opacity 0.3s ease",
									fontWeight: 500,
								}}
							>
								Toca para ampliar
							</Typography>
						</Box>
					</Fade>
				)}
			</Box>

			{isDemo && cardData && (
				<Button
					variant="contained"
					disableElevation
					onClick={() =>
						requireAuth("Crea una cuenta para guardar tu tarjeta.")
					}
					sx={{
						mt: 3,
						zIndex: 1,
						textTransform: "none",
						fontWeight: 700,
						color: "#fff !important",
						border: "none !important",
						background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
						borderRadius: "12px",
						px: 2.5,
						"&:hover": {
							background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
							color: "#fff !important",
						},
					}}
				>
					Guardar mi tarjeta
				</Button>
			)}

			{/* Info contextual bajo la tarjeta */}
			{cardData && !isDemo && countries.length === 0 && (
				<Fade in timeout={800}>
					<Box
						sx={{
							mt: 4,
							textAlign: "center",
							zIndex: 1,
							position: "relative",
						}}
					>
						<Typography sx={{ color: "#94a3b8", fontSize: "0.85rem", mb: 1 }}>
							Aún no tienes sellos en tu pasaporte
						</Typography>
						<Button
							onClick={() => navigate("/passport")}
							sx={{
								color: "#6366F1",
								textTransform: "none",
								fontWeight: 600,
								fontSize: "0.85rem",
								"&:hover": {
									backgroundColor: "rgba(99,102,241,0.06)",
								},
							}}
						>
							Añadir primer destino
						</Button>
					</Box>
				</Fade>
			)}

			{/* Creador */}
			<TravelCardCreator
				open={creatorOpen}
				onClose={() => setCreatorOpen(false)}
				onSave={handleSaveCard}
				userName={userName}
				existingCard={cardData}
			/>

			{/* Modal premium */}
			{cardData && (
				<TravelCardModal
					open={modalOpen}
					onClose={() => setModalOpen(false)}
					cardData={cardData}
					countries={countries}
					cardStyle={cardStyle}
					onSnackbar={setSnackbar}
					onRequireAuth={() =>
						requireAuth("Crea una cuenta para descargar tu tarjeta.")
					}
				/>
			)}

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					severity={snackbar.severity}
					sx={{
						...(snackbar.severity === "success"
							? {
									backgroundColor: "#f5f3ff",
									color: "#4f46e5",
									border: "1px solid rgba(99, 102, 241, 0.25)",
									boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
									"& .MuiAlert-icon": { color: "#6366F1" },
								}
							: {}),
						...(snackbar.severity === "error"
							? {
									backgroundColor: "#fef2f2",
									color: "#b91c1c",
									border: "1px solid rgba(185, 28, 28, 0.2)",
								}
							: {}),
					}}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default TravelCardPage;
